import * as React from 'react';

import { ChevronDownIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

interface SimpleDateTimePickerProps {
  label: string;
  value?: Date;
  onChange: (date: Date | undefined) => void;
  id?: string;
}

export function SimpleDateTimePicker({ label, value, onChange, id }: SimpleDateTimePickerProps) {
  const [open, setOpen] = React.useState(false);
  const [date, setDate] = React.useState<Date | undefined>(value);
  const [time, setTime] = React.useState<string>(
    value
      ? value.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' })
      : '00:00',
  );

  // Sync with external value changes
  React.useEffect(() => {
    setDate(value);
    if (value) {
      setTime(
        value.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' }),
      );
    }
  }, [value]);

  const handleDateSelect = (newDate: Date | undefined) => {
    setDate(newDate);
    setOpen(false);

    if (newDate) {
      // Combine date with current time
      const [hours, minutes] = time.split(':').map(Number);
      const combined = new Date(newDate);
      combined.setHours(hours, minutes, 0, 0);
      onChange(combined);
    } else {
      onChange(undefined);
    }
  };

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = e.target.value;
    setTime(newTime);

    if (date) {
      // Combine current date with new time
      const [hours, minutes] = newTime.split(':').map(Number);
      const combined = new Date(date);
      combined.setHours(hours, minutes, 0, 0);
      onChange(combined);
    }
  };

  return (
    <div className='flex gap-4'>
      <div className='flex flex-1 flex-col gap-3'>
        <Label htmlFor={id} className='px-1'>
          {label}
        </Label>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant='outline'
              id={id}
              className='w-full justify-between font-normal'
              type='button'
            >
              {date
                ? date.toLocaleDateString('en-US', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                  })
                : 'Select date'}
              <ChevronDownIcon className='h-4 w-4' />
            </Button>
          </PopoverTrigger>
          <PopoverContent className='w-auto overflow-hidden p-0' align='start'>
            <Calendar
              mode='single'
              selected={date}
              captionLayout='dropdown'
              onSelect={handleDateSelect}
              fromYear={2020}
              toYear={2030}
            />
          </PopoverContent>
        </Popover>
      </div>
      <div className='flex flex-col gap-3'>
        <Label htmlFor={`${id}-time`} className='invisible px-1'>
          Time
        </Label>
        <Input
          type='time'
          id={`${id}-time`}
          value={time}
          onChange={handleTimeChange}
          className='bg-background appearance-none [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none'
        />
      </div>
    </div>
  );
}
